import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { MasterEmpresaService } from '../../../services/master/master-empresa.service';
import {
  FiltroPlanosMaster,
  MasterPlanoAtivoDTO,
  OrdemPlanosMaster,
  TipoPlanoMaster,
  CategoriaPlanoMaster,
} from '../../../models/master/master-plano.model';
import {
  MasterCobrancaDTO,
  MasterDashboardAssinaturasDTO,
  MasterEmpresaEmRiscoDTO,
  TipoCobrancaMaster,
  TipoRiscoMaster,
} from '../../../models/master/master-dashboard.model';
import { PeriodoFiltro, TipoPeriodo } from '../../../models/dashboard/periodo-filtro.model';
import { fmtDataIso, pad, paraDataIso } from '../../../shared/utils/periodo.utils';
import { ultimos30 } from '../../../shared/utils/periodo-core.util';

/** Estado das duas maquinas de carga desta tela (a faixa e o drill-down). */
type EstadoDashboard = 'carregando' | 'pronto' | 'erro';
type EstadoRisco = 'carregando' | 'pronto' | 'vazio' | 'erro';

/**
 * Area Master — Assinaturas.
 *
 * Dois andares independentes na mesma tela:
 *  - a FAIXA DE INDICADORES (`GET /master/planos/dashboard`), que responde quanto entra, quem
 *    esta usando e quem esta em risco;
 *  - a CARTEIRA paginada (`GET /master/planos`), que responde com quem falar.
 *
 * As duas consultas nao se conhecem: erro numa nao derruba a outra, e trocar o periodo
 * recarrega SO a faixa — a carteira e a foto de hoje e o periodo nao a afeta.
 *
 * ARMADILHAS QUE ESTA TELA EXISTE PARA NAO COMETER (contrato §1):
 *  - tres pares que nunca somam e um recorte contido em outro: nenhum total e derivado aqui;
 *  - a lista de inadimplencia pode conter cliente EM DIA (renovacao antecipada, limitacao L1):
 *    o card e indicacao a conferir, nunca fato, e nao oferece acao de cobranca;
 *  - `de`/`ate` viajam em ISO `yyyy-MM-dd`, convertidos por `paraDataIso` — o `app-periodo-filtro`
 *    emite `dd/MM/yyyyTHH:mm`, e mandar isso cru responde 500.
 */
@Component({
  selector: 'app-master-planos',
  templateUrl: './master-planos.component.html',
  styleUrls: ['../master-listagem.scss', './master-planos-dashboard.scss'],
  providers: [MessageService],
})
export class MasterPlanosComponent implements OnInit, OnDestroy {
  // Objetos de traducao
  texts: any = {};
  labels: any = {};
  tooltips: any = {};
  planosTexts: any = {};
  planosLabels: any = {};

  /** Janela em que o suporte trabalha a renovacao — e o padrao do filtro "vencendo". */
  readonly DIAS_PARA_VENCER = 15;
  /** A partir daqui o cliente precisa ser avisado: sobra pouco espaco para a proxima venda. */
  readonly PERCENTUAL_DE_ALERTA = 90;

  planos: MasterPlanoAtivoDTO[] = [];
  planosLoading = true;
  planosFirst = 0;
  planosRows = 10;
  planosTotal = 0;
  filtroPlanoBusca = '';
  filtroPlanos: FiltroPlanosMaster = 'TODOS';
  planosOrdenarPor: OrdemPlanosMaster = 'VENCIMENTO';
  planosAscendente = true;

  /** Coluna da tabela -> coluna de ordenacao do backend (as duas ultimas sao calculadas). */
  private readonly COLUNA_PARA_ORDEM: Record<string, OrdemPlanosMaster> = {
    empresaNome: 'EMPRESA',
    venceEm: 'VENCIMENTO',
    percentualUsado: 'USO_DO_LIMITE',
    vendasNoCiclo: 'VENDAS',
  };

  filtrosPlanos: { label: string; value: FiltroPlanosMaster }[] = [];

  /** Eixo 2 — periodicidade. `null` = sem recorte, e o parametro NAO viaja (RF04). */
  tipoPlanoSelecionado: TipoPlanoMaster | null = null;

  /** Opcoes do dropdown. Montadas em `loadTranslations()` para sobreviverem a troca de idioma. */
  tiposDePlano: { label: string; value: TipoPlanoMaster }[] = [];

  /** Lista FECHADA, na ordem do enum do backend. Sem valor "TODOS" — a ausencia e o "todos". */
  private readonly TIPOS_DE_PLANO: TipoPlanoMaster[] = [
    'MENSAL', 'TRIMESTRAL', 'SEMESTRAL', 'ANUAL', 'ESPECIAL',
  ];

  /** Eixo 3 — faixa do plano (Bronze, Prata, Ouro). `null` = todas, e o parametro NAO viaja. */
  categoriaSelecionada: CategoriaPlanoMaster | null = null;

  /** Opcoes do dropdown de faixa, montadas em `loadTranslations()` como as de periodicidade. */
  categoriasDePlano: { label: string; value: CategoriaPlanoMaster }[] = [];

  /** Lista FECHADA, na ordem do enum `CategoriaPlanoMaster` do backend. */
  private readonly CATEGORIAS_DE_PLANO: CategoriaPlanoMaster[] = ['SEM_INICIAL', 'BRONZE', 'PRATA', 'OURO'];

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // =========================================================================
  // Faixa de indicadores
  // =========================================================================

  /**
   * Presets do filtro de periodo — lista FECHADA.
   *
   * Ficam de fora `showAll` (emite 01/01/2000 -> 31/12/3000, que o backend recorta para um
   * periodo inteiramente no futuro, com tudo zerado), `untilToday` (cairia sempre no recorte de
   * 366 dias, deixando o aviso de ajuste permanente), `untilMonthEnd` e `nextMonth` (janelas
   * futuras: sem recebimento, sem uso e sem risco).
   */
  readonly PRESETS_DASHBOARD: TipoPeriodo[] = [
    'today', 'thisWeek', 'thisMonth', 'prevMonth', 'last30',
  ];

  dashboard: MasterDashboardAssinaturasDTO | null = null;
  dashboardEstado: EstadoDashboard = 'carregando';
  dashboardErro = '';
  /** Faixa recolhida: quem so quer a tabela nao precisa rolar a faixa toda. */
  recolhida = false;
  periodoTexto = '';
  /** O periodo ecoado difere do enviado (recorte de 366 dias) ou nao foi possivel enviar. */
  periodoAjustado = false;
  /** Janela do bloco de vencimentos do mes — rotulo, nao numero de negocio. */
  mesUltimoDia = '';
  mesNumero = '';

  private ultimoDe: string | null = null;
  private ultimoAte: string | null = null;

  private readonly CHAVE_RECOLHIDA = 'master-planos:dashboard-recolhido';

  // =========================================================================
  // Drill-down de risco
  // =========================================================================

  riscoDialogVisivel = false;
  riscoTipo: TipoRiscoMaster = 'INADIMPLENCIA';
  riscoLinhas: MasterEmpresaEmRiscoDTO[] = [];
  riscoEstado: EstadoRisco = 'carregando';
  riscoErro = '';
  riscoBusca = '';
  riscoTotal = 0;
  riscoFirst = 0;
  /** Tamanho ECOADO pelo envelope — o backend satura silenciosamente o que for enviado. */
  riscoSize = 10;
  private riscoPagina = 0;
  /**
   * Ultimo termo que REALMENTE virou consulta. E o que o debounce compara para nao repetir
   * requisicao a cada tecla que nao muda o texto.
   *
   * Existe como CAMPO, e nao como `distinctUntilChanged()`, porque a memoria do operador do RxJS
   * nao pode ser zerada: com ela, fechar o dialogo e repetir o MESMO termo era engolido (o input
   * mostrava a busca e a tabela vinha sem filtro, com o aviso do CA06 aceso sobre um total que
   * batia). Aqui o reset acompanha o de `riscoBusca`, nos dois pontos que o zeram.
   */
  private ultimaBuscaRiscoConsultada = '';

  // =========================================================================
  // Drill-down de dinheiro (recebido no periodo / cobranca emitida e nao paga)
  // =========================================================================

  cobrancaDialogVisivel = false;
  cobrancaTipo: TipoCobrancaMaster = 'RECEBIDAS';
  cobrancaLinhas: MasterCobrancaDTO[] = [];
  cobrancaEstado: EstadoRisco = 'carregando';
  cobrancaErro = '';

  private searchPlanos$ = new Subject<string>();
  private searchRisco$ = new Subject<string>();
  private langChangeSub!: Subscription;

  @ViewChild('carteiraCard') carteiraCard?: ElementRef<HTMLElement>;

  constructor(
    private messageService: MessageService,
    private masterEmpresaService: MasterEmpresaService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.setupSearchDebounce();
    this.setupBuscaRiscoDebounce();
    this.recolhida = this.lerEstadoRecolhido();
    this.calcularJanelaDoMes();
    this.findPlanos();
    this.carregarDashboardComPeriodoPadrao();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
    this.searchPlanos$.complete();
    this.searchRisco$.complete();
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('master.planos.texts.titulo_pagina'),
      subtitulo: this.translate.instant('master.planos.texts.subtitulo_pagina'),
    };

    this.labels = {
      acoes: this.translate.instant('master.empresas.labels.acoes'),
      itens_por_pagina: this.translate.instant('master.empresas.labels.itens_por_pagina'),
    };

    this.tooltips = {
      abrir: this.translate.instant('master.empresas.tooltips.abrir'),
    };

    this.planosTexts = {
      titulo: this.translate.instant('master.planos.texts.titulo'),
      explicacao: this.translate.instant('master.planos.texts.explicacao'),
      nenhum: this.translate.instant('master.planos.texts.nenhum'),
      dica_busca: this.translate.instant('master.planos.texts.dica_busca'),
      carregando: this.translate.instant('master.planos.texts.carregando'),
      pesquisar: this.translate.instant('master.planos.placeholders.pesquisar'),
      em_dia: this.translate.instant('master.planos.texts.em_dia'),
      vencida: this.translate.instant('master.planos.texts.vencida'),
      nao_vence: this.translate.instant('master.planos.texts.nao_vence'),
      sem_limite: this.translate.instant('master.planos.texts.sem_limite'),
      vencido_ha: this.translate.instant('master.planos.texts.vencido_ha'),
      dias: this.translate.instant('master.planos.texts.dias'),
      negociado: this.translate.instant('master.planos.texts.negociado'),
      limite_do_gratuito: this.translate.instant('master.planos.texts.limite_do_gratuito'),
      voltou_ao_inicial: this.translate.instant('master.planos.texts.voltou_ao_inicial'),
      voltou_ao_inicial_dica: this.translate.instant('master.planos.texts.voltou_ao_inicial_dica'),
      valor_renovacao: this.translate.instant('master.planos.texts.valor_renovacao'),
      dica_sem_vencimento: this.translate.instant('master.planos.texts.dica_sem_vencimento'),
      // Estado neutro do eixo de periodicidade: e o placeholder do dropdown, e nao uma opcao
      // da lista — o valor "TODOS" nao existe no enum do backend.
      periodicidade_todas: this.translate.instant('master.planos.placeholders.periodicidade'),
      categoria_todas: this.translate.instant('master.planos.placeholders.categoria'),
    };

    this.planosLabels = {
      empresa: this.translate.instant('master.planos.labels.empresa'),
      plano: this.translate.instant('master.planos.labels.plano'),
      situacao: this.translate.instant('master.planos.labels.situacao'),
      vence_em: this.translate.instant('master.planos.labels.vence_em'),
      limite: this.translate.instant('master.planos.labels.limite'),
      vendido: this.translate.instant('master.planos.labels.vendido'),
      uso: this.translate.instant('master.planos.labels.uso'),
      periodicidade: this.translate.instant('master.planos.labels.periodicidade'),
      categoria: this.translate.instant('master.planos.labels.categoria'),
    };

    // ORDEM IMPORTA: a entrada EM_DIA le `this.planosTexts.em_dia`, atribuido no bloco acima.
    // Montar `filtrosPlanos` antes de `planosTexts` produz `label: undefined` — botao em branco,
    // sem erro de compilacao.
    this.filtrosPlanos = [
      { label: this.translate.instant('master.planos.filtros.todos'), value: 'TODOS' },
      // D-FE1: o rotulo do filtro "Em dia" e A MESMA STRING do badge da coluna "Situacao".
      // Ponto unico de verdade DE PROPOSITO: o filtro EM_DIA traz o plano ESPECIAL (contrato,
      // "EM_DIA inclui toda cortesia e concessao manual") e o badge dele passou a dizer
      // "Em dia" por decisao do usuario (FE8). Duas chaves separadas permitiriam que uma edicao
      // de traducao recriasse a contradicao que esta feature veio corrigir.
      { label: this.planosTexts.em_dia, value: 'EM_DIA' },
      { label: this.translate.instant('master.planos.filtros.vencendo'), value: 'VENCENDO' },
      { label: this.translate.instant('master.planos.filtros.no_limite'), value: 'NO_LIMITE' },
      { label: this.translate.instant('master.planos.filtros.em_carencia'), value: 'EM_CARENCIA' },
      { label: this.translate.instant('master.planos.filtros.vencidos'), value: 'VENCIDOS' },
      // Drill-down do card "Previsao de renovacao": mesmo predicado do card no backend.
      { label: this.translate.instant('master.planos.filtros.renovacao'), value: 'RENOVACAO' },
      // Drill-down do card de MRR: as "ativas" dele (pagas, vigentes, fora do ESPECIAL).
      { label: this.translate.instant('master.planos.filtros.pagas'), value: 'PAGAS' },
    ];

    // O MESMO helper que rotula a coluna "Plano" (DA-03): o que o dropdown oferece e o que a
    // tabela mostra nao podem divergir — e essa e a unica coluna que confirma o recorte novo.
    this.tiposDePlano = this.TIPOS_DE_PLANO.map((tipo) => ({
      label: this.rotuloDaPeriodicidade(tipo),
      value: tipo,
    }));

    // Faixas com os mesmos nomes da ficha da empresa (plano especial): Bronce, Plata, Oro.
    // SEM_INICIAL nao e faixa, e por isso tem chave propria.
    this.categoriasDePlano = this.CATEGORIAS_DE_PLANO.map((categoria) => ({
      label: categoria === 'SEM_INICIAL'
        ? this.translate.instant('master.planos.categorias.sem_inicial')
        : this.translate.instant(`master.especial.categorias.${categoria.toLowerCase()}`),
      value: categoria,
    }));
  }

  // =========================================================================
  // Busca
  // =========================================================================

  private setupSearchDebounce(): void {
    this.searchPlanos$
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(() => {
        this.planosFirst = 0;
        this.findPlanos(0);
      });
  }

  onSearchPlanoChange(valor: string): void {
    this.filtroPlanoBusca = valor || '';
    this.searchPlanos$.next(this.filtroPlanoBusca);
  }

  // =========================================================================
  // Planos (carteira de assinaturas)
  // =========================================================================

  findPlanos(page: number = 0, size: number = this.planosRows): void {
    this.planosLoading = true;
    this.masterEmpresaService
      .listarPlanosAtivos(
        this.filtroPlanoBusca,
        this.filtroPlanos,
        this.planosOrdenarPor,
        this.planosAscendente,
        page,
        size,
        this.PERCENTUAL_DE_ALERTA,
        this.DIAS_PARA_VENCER,
        this.tipoPlanoSelecionado,
        this.categoriaSelecionada,
      )
      .subscribe({
        next: (resposta) => {
          this.planos = resposta.content || [];
          this.planosTotal = resposta.totalElements || 0;
          this.planosFirst = page * size;
          this.planosLoading = false;
        },
        error: () => {
          this.planos = [];
          this.planosTotal = 0;
          this.planosLoading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: this.translate.instant('master.planos.mensagens.erro_carregar'),
          });
        },
      });
  }

  /**
   * Troca o recorte da carteira. Cada filtro tem a ordem que faz sentido para ele: quem procura
   * vencimento quer os mais proximos no topo; quem procura limite quer os mais apertados.
   *
   * FE9: `EM_DIA` cai no ramo `else` e recebe VENCIMENTO ascendente. E deliberado — `EM_DIA` e o
   * recorte que MAIS devolve linhas (quase toda a base paga), e nele a ordem padrao importa mais
   * do que nos outros.
   *
   * NAO limpa `tipoPlanoSelecionado`: os dois eixos sao independentes (RF03). A limpeza pertence
   * ao clique no CARD, e so a ele (ver `onCardCarteiraClick`) — este metodo e COMPARTILHADO pelos
   * dois caminhos, e limpar aqui apagaria a periodicidade a cada troca de botao.
   */
  onFiltroPlanosChange(filtro: FiltroPlanosMaster | null): void {
    this.filtroPlanos = filtro ?? 'TODOS';
    if (this.filtroPlanos === 'NO_LIMITE') {
      this.planosOrdenarPor = 'USO_DO_LIMITE';
      this.planosAscendente = false;
    } else {
      this.planosOrdenarPor = 'VENCIMENTO';
      this.planosAscendente = true;
    }
    this.planosFirst = 0;
    this.findPlanos(0);
  }

  /**
   * Troca o recorte de PERIODICIDADE.
   *
   * Eixo INDEPENDENTE do de situacao (RF03): nao mexe no `filtroPlanos` nem na ordenacao —
   * periodicidade e recorte, nao criterio de leitura (D-FE6).
   * O `showClear` do p-dropdown emite `null`: e o "voltar para todas", e o parametro deixa de
   * viajar. Nenhum valor `TODOS` e inventado (ele nao existe no enum do backend).
   */
  onTipoPlanoChange(tipo: TipoPlanoMaster | null): void {
    this.tipoPlanoSelecionado = tipo ?? null;   // `?? null` normaliza o `undefined` do clear
    this.planosFirst = 0;                       // RF11
    this.findPlanos(0);
  }

  /**
   * Troca o recorte de FAIXA do plano. Mesmo comportamento de `onTipoPlanoChange`: eixo
   * independente, nao mexe na situacao nem na ordenacao, e o `showClear` (null) volta para todas.
   */
  onCategoriaChange(categoria: CategoriaPlanoMaster | null): void {
    this.categoriaSelecionada = categoria ?? null;
    this.planosFirst = 0;
    this.findPlanos(0);
  }

  onPlanosPageChange(evento: any): void {
    this.planosRows = evento.rows;
    this.findPlanos(evento.page, evento.rows);
  }

  onPlanosRowsPerPageChange(): void {
    this.planosFirst = 0;
    this.findPlanos(0, this.planosRows);
  }

  /** Ordenacao server-side: as colunas calculadas so o banco sabe ordenar. */
  onSortPlanos(evento: SortEvent): void {
    const campo = (evento.field || 'venceEm') as string;
    const ordem = evento.order === -1 ? false : true;
    const coluna = this.COLUNA_PARA_ORDEM[campo] ?? 'VENCIMENTO';
    if (coluna === this.planosOrdenarPor && ordem === this.planosAscendente) return;
    this.planosOrdenarPor = coluna;
    this.planosAscendente = ordem;
    this.planosFirst = 0;
    this.findPlanos(0);
  }

  /** Campo que a tabela exibe como ordenado — o inverso do mapa acima. */
  get campoOrdenadoPlanos(): string {
    switch (this.planosOrdenarPor) {
      case 'USO_DO_LIMITE': return 'percentualUsado';
      case 'VENDAS': return 'vendasNoCiclo';
      case 'EMPRESA': return 'empresaNome';
      default: return 'venceEm';
    }
  }

  /**
   * Rotulo da situacao da assinatura: leitura LITERAL de `vigente` — SO esse campo.
   *
   * NAO e o mesmo predicado de `EM_DIA` do backend. La sao DOIS termos
   * (`NOT planoGratuito AND vigente` — API_CONTRACT_master_planos_ativos.md, campo `vigente`);
   * aqui se le apenas um. Na pratica isso diverge no PLANO GRATUITO: dentro da janela ele tem
   * `vigente = true` e por isso esta linha mostra o badge "Em dia", mas o filtro `EM_DIA` (que usa
   * o predicado completo do backend) exclui o plano gratuito — clicar no botao "Em dia" faz essa
   * linha sumir da tela. Registrado e NAO corrigido aqui (REVIEW_FE M-02): a decisao sobre o
   * rotulo do plano gratuito e do usuario e sera tratada em outra feature; nao antecipar.
   *
   * O ramo do `especial` saiu (FE8, decisao do usuario): o perpetuo e sempre vigente, logo sempre
   * "Em dia" — coerente com o filtro que o traz. A informacao de que ele nao tem vencimento NAO
   * se perde: a coluna "Vence em" continua mostrando "Nao vence" e a "Limite de vendas" continua
   * mostrando "Sem limite". A linha diz as tres coisas ao mesmo tempo, sem se contradizer (CA08).
   */
  situacaoDoPlano(plano: MasterPlanoAtivoDTO): string {
    return plano.vigente ? this.planosTexts.em_dia : this.planosTexts.vencida;
  }

  /**
   * Rotulo traduzido de uma periodicidade. Sem traducao, mostra o PROPRIO CODIGO — nunca a chave.
   * Mesma tecnica que a ficha da empresa ja usa em `master-empresa-detalhe.component.ts`.
   */
  private rotuloDaPeriodicidade(tipoPlano: string): string {
    const chave = `master.planos.tipos.${tipoPlano.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? tipoPlano : traduzido;
  }

  /**
   * Periodicidade da linha, exibida sob o nome do plano (P-FE1/RF07).
   *
   * Sem esta coluna o eixo novo e um filtro que NENHUMA coluna confirma: se o parametro parar de
   * ser enviado, a tela responde 200, sem erro, com linhas demais — e fica IDENTICA.
   */
  periodicidadeDoPlano(plano: MasterPlanoAtivoDTO): string {
    if (!plano?.tipoPlano) return '';
    return this.rotuloDaPeriodicidade(plano.tipoPlano);
  }

  /**
   * A combinacao escolhida e vazia POR CONSTRUCAO, nao por erro: o plano ESPECIAL nao tem
   * vencimento, logo nunca esta "vencendo", "em carencia" nem "vencido" (contrato: 200 com
   * lista vazia).
   * Sem esta explicacao o operador le "Nenhuma assinatura encontrada" e conclui que o sistema
   * esta errado — e o mantra do projeto diz o contrario.
   *
   * NAO cobre `NO_LIMITE` x `ESPECIAL` de proposito (DA-07): aquele recorte so e vazio enquanto a
   * tela mandar `percentualAlerta = 90`, o que e uma constante DESTA tela e nao uma propriedade
   * do dominio. Nele a dica generica continua valendo e continua verdadeira.
   */
  get recorteVazioPorConstrucao(): boolean {
    return this.tipoPlanoSelecionado === 'ESPECIAL'
      && (this.filtroPlanos === 'VENCENDO'
        || this.filtroPlanos === 'EM_CARENCIA'
        || this.filtroPlanos === 'VENCIDOS'
        || this.filtroPlanos === 'RENOVACAO'
        || this.filtroPlanos === 'PAGAS');
  }

  /** Vermelho a partir do alerta: e o cliente que vai ser barrado na proxima venda. */
  limiteEmAlerta(plano: MasterPlanoAtivoDTO): boolean {
    return !plano.especial && plano.percentualUsado >= this.PERCENTUAL_DE_ALERTA;
  }

  /** Amarelo quando vence dentro da janela de trabalho do suporte (ou ja venceu). */
  vencimentoEmAlerta(plano: MasterPlanoAtivoDTO): boolean {
    if (plano.especial || plano.diasParaVencer == null) return false;
    return plano.diasParaVencer <= this.DIAS_PARA_VENCER;
  }

  abrirEmpresaDoPlano(plano: MasterPlanoAtivoDTO): void {
    if (plano?.empresaId == null) return;
    this.router.navigate(['/master/empresas', plano.empresaId], {
      queryParams: { origem: 'planos' },
    });
  }

  // =========================================================================
  // Faixa de indicadores — carga
  // =========================================================================

  /**
   * Carga inicial da faixa.
   *
   * O `app-periodo-filtro` com `persistir=false` NAO emite no `ngOnInit`, entao a tela calcula
   * o periodo padrao por conta propria. Calcular aqui tambem alinha o rotulo do botao ("Ultimos
   * 30 dias") com a janela realmente consultada: deixar o backend aplicar o default daria
   * 31 dias, e o botao diria uma coisa enquanto o rodape diria outra.
   */
  private carregarDashboardComPeriodoPadrao(): void {
    const { start, end } = ultimos30(new Date());
    this.carregarDashboard(fmtDataIso(start), fmtDataIso(end));
  }

  /** Unico caminho de carga da faixa — usado no init, na troca de periodo e no "tentar de novo". */
  carregarDashboard(de: string | null, ate: string | null): void {
    this.dashboardEstado = 'carregando';
    this.dashboardErro = '';
    this.ultimoDe = de;
    this.ultimoAte = ate;

    this.masterEmpresaService
      .consultarDashboard({
        de,
        ate,
        diasParaVencer: this.DIAS_PARA_VENCER,
        percentualAlerta: this.PERCENTUAL_DE_ALERTA,
      })
      .subscribe({
        next: (resposta) => {
          this.dashboard = resposta;
          // Duas razoes para o periodo exibido nao ser o pedido: o par foi omitido (data que
          // nao convertemos, entao vale o default do backend) ou o backend recortou a janela
          // para o maximo de 366 dias. Nos dois casos o rodape avisa, e sempre com o ECO.
          const parOmitido = !de || !ate;
          this.periodoAjustado = parOmitido
            || resposta.periodoDe !== de
            || resposta.periodoAte !== ate;
          this.dashboardEstado = 'pronto';
        },
        error: (err: HttpErrorResponse) => {
          this.dashboard = null;
          this.dashboardErro = this.mensagemDeErroDoDashboard(err);
          this.dashboardEstado = 'erro';
        },
      });
  }

  /** Botao "tentar novamente" da faixa: repete exatamente a janela que falhou. */
  recarregarDashboard(): void {
    this.carregarDashboard(this.ultimoDe, this.ultimoAte);
  }

  /**
   * Troca de periodo: recarrega SO a faixa. A carteira e a composicao da base sao a foto de
   * hoje e nao mudam de valor.
   */
  onPeriodoDashboardChange(periodo: PeriodoFiltro): void {
    this.periodoTexto = periodo?.texto || '';
    const de = paraDataIso(periodo?.dataInicio);
    const ate = paraDataIso(periodo?.dataFim);

    if (de == null || ate == null) {
      // Valor nao reconhecido: omitimos o par inteiro em vez de mandar lixo e receber 500 — o
      // backend aplica o default, e `carregarDashboard` liga o aviso de periodo ajustado porque
      // o rodape vai mostrar uma janela diferente da que o operador escolheu.
      this.carregarDashboard(null, null);
      return;
    }

    this.carregarDashboard(de, ate);
  }

  /**
   * Status HTTP -> mensagem ao usuario, para as DUAS rotas do dashboard.
   *
   * Parametro malformado responde 500 hoje, embora o contrato mire 400. Por isso 400 e 500 caem
   * na MESMA chave: quando o handler global do backend for corrigido, a transicao e transparente
   * e nenhuma linha daqui muda. Escrever `if (status === 400)` criaria um ramo que hoje nao e
   * exercitado e que passaria a ser sem ninguem perceber.
   */
  private mensagemDeErroDoDashboard(err: HttpErrorResponse): string {
    const status = err?.status ?? 0;

    if (status === 401 || status === 403) {
      return this.translate.instant('master.planos.dashboard.mensagens.erro_permissao');
    }

    if (status === 422) {
      // Ja vem no idioma da sessao.
      return err?.error?.message
        || this.translate.instant('master.planos.dashboard.mensagens.erro_consultar');
    }

    if (status === 0) {
      // Unica falha destas rotas cuja causa a tela conhece com certeza.
      return this.translate.instant('master.planos.dashboard.mensagens.erro_conexao');
    }

    return this.translate.instant('master.planos.dashboard.mensagens.erro_consultar');
  }

  // =========================================================================
  // Faixa de indicadores — interacao
  // =========================================================================

  /**
   * Clique num card da Faixa A: aplica o filtro equivalente na carteira e rola ate ela.
   *
   * Os tres cards usam o predicado LITERAL dos filtros da tabela — e por isso que o numero do
   * card e o `totalElements` da carteira sao o mesmo numero, e e isso que torna a faixa
   * auditavel. Clicar de novo volta ao estado neutro.
   *
   * FE7/RN08: os cards NAO tem eixo de periodicidade. Com `tipoPlano` ativo a tabela vira um
   * SUBCONJUNTO do card, e a faixa acesa passa a dizer 18 com a tabela dizendo 4 — contradicao
   * visivel na mesma tela. Por isso a periodicidade e limpa por ATRIBUICAO DIRETA, e ANTES de
   * trocar o filtro: `onFiltroPlanosChange` ja dispara a consulta, entao a ordem garante UMA
   * consulta so, ja sem o parametro. Chamar `onTipoPlanoChange(null)` aqui dispararia uma
   * segunda requisicao, a primeira ainda com o filtro antigo.
   */
  onCardCarteiraClick(filtro: FiltroPlanosMaster): void {
    const proximo: FiltroPlanosMaster = this.filtroPlanos === filtro ? 'TODOS' : filtro;
    this.tipoPlanoSelecionado = null;     // ANTES — ver acima
    this.categoriaSelecionada = null;     // a faixa tambem nao existe nos cards
    this.onFiltroPlanosChange(proximo);   // unica consulta
    this.carteiraCard?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Recolher e preferencia de quem so quer a tabela — sobrevive ao recarregar a pagina. */
  alternarRecolhida(): void {
    this.recolhida = !this.recolhida;
    this.gravarEstadoRecolhido(this.recolhida);
  }

  private lerEstadoRecolhido(): boolean {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false;
      return window.localStorage.getItem(this.CHAVE_RECOLHIDA) === 'true';
    } catch {
      return false;
    }
  }

  private gravarEstadoRecolhido(valor: boolean): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.setItem(this.CHAVE_RECOLHIDA, String(valor));
    } catch {
      // Navegador com storage bloqueado: a faixa apenas deixa de lembrar a preferencia.
    }
  }

  /**
   * Janela do bloco de vencimentos do mes: dia 1 ao ultimo dia do mes CORRENTE.
   *
   * E rotulo, nao numero de negocio — o operador precisa ler a janela no titulo em vez de
   * deduzi-la, porque esta e a terceira janela de "vencimento" da tela.
   */
  private calcularJanelaDoMes(): void {
    const hoje = new Date();
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate();
    this.mesUltimoDia = String(ultimoDia);
    this.mesNumero = pad(hoje.getMonth() + 1);
  }

  // =========================================================================
  // Drill-down de risco
  // =========================================================================

  private setupBuscaRiscoDebounce(): void {
    this.searchRisco$
      .pipe(debounceTime(600))
      .subscribe((termo) => {
        // Debounce pendente disparado DEPOIS do fechamento: nao consulta risco com o dialogo
        // fechado (o resultado so alimentaria estado que a proxima abertura sobrescreve).
        if (!this.riscoDialogVisivel) return;
        if (termo === this.ultimaBuscaRiscoConsultada) return;
        this.ultimaBuscaRiscoConsultada = termo;
        this.carregarRisco(0);
      });
  }

  /**
   * Abre a lista de quem esta em risco.
   *
   * E o unico caminho possivel a partir do card: a lista existe justamente para o operador
   * conferir linha a linha antes de ligar, porque o card pode conter cliente em dia.
   */
  onCardRiscoClick(tipo: TipoRiscoMaster): void {
    if (this.dashboardEstado !== 'pronto' || !this.dashboard) return;
    this.riscoTipo = tipo;
    this.riscoBusca = '';
    this.ultimaBuscaRiscoConsultada = '';
    this.riscoSize = 10;
    this.riscoDialogVisivel = true;
    this.carregarRisco(0);
  }

  carregarRisco(page: number): void {
    if (!this.dashboard) return;
    this.riscoEstado = 'carregando';
    this.riscoErro = '';
    this.riscoPagina = page;

    this.masterEmpresaService
      .listarEmpresasEmRisco({
        tipoRisco: this.riscoTipo,
        // ECOS do dashboard, nunca constante local: e o que faz o total bater com o card.
        diasSemPagar: this.dashboard.risco.diasSemPagar,
        diasSemMovimentar: this.dashboard.risco.diasSemMovimentar,
        busca: this.riscoBusca,
        page,
        size: this.riscoSize,
      })
      .subscribe({
        next: (resposta) => {
          this.riscoLinhas = resposta.content ?? [];
          this.riscoTotal = resposta.totalElements ?? 0;
          // Envelope ECOADO: o backend satura `size` e `page` em silencio.
          this.riscoSize = resposta.size ?? this.riscoSize;
          this.riscoFirst = (resposta.number ?? 0) * this.riscoSize;
          this.riscoEstado = this.riscoTotal === 0 ? 'vazio' : 'pronto';
        },
        error: (err: HttpErrorResponse) => {
          this.riscoLinhas = [];
          this.riscoTotal = 0;
          this.riscoErro = this.mensagemDeErroDoDashboard(err);
          this.riscoEstado = 'erro';
        },
      });
  }

  onRiscoPageChange(evento: any): void {
    this.riscoSize = evento.rows;
    this.carregarRisco(evento.page);
  }

  onBuscaRiscoChange(valor: string): void {
    this.riscoBusca = valor || '';
    this.searchRisco$.next(this.riscoBusca);
  }

  recarregarRisco(): void {
    this.carregarRisco(this.riscoPagina);
  }

  fecharRisco(): void {
    this.riscoDialogVisivel = false;
    this.riscoLinhas = [];
    this.riscoTotal = 0;
    this.riscoFirst = 0;
    this.riscoBusca = '';
    this.ultimaBuscaRiscoConsultada = '';
    this.riscoEstado = 'carregando';
  }

  abrirEmpresaEmRisco(linha: MasterEmpresaEmRiscoDTO): void {
    if (linha?.empresaId == null) return;
    this.router.navigate(['/master/empresas', linha.empresaId], {
      queryParams: { origem: 'planos' },
    });
  }

  /** Com busca, o total deixa de bater com o card — de proposito, e a tela avisa. */
  get buscaQuebraIgualdade(): boolean {
    return this.riscoBusca.trim().length > 0;
  }

  /** Numero de colunas do drill-down, calculado (as duas especificas trocam por tipo de risco). */
  get colunasDoDrillDown(): number {
    const comuns = 5;      // empresa, plano, ultimo pagamento, telefone, valor mensal
    const especificas = 2; // INADIMPLENCIA: venceu em + dias vencido / ABANDONO: as de movimento
    return comuns + especificas;
  }

  /**
   * `empresaNome` vem nulo quando o cadastro esta sem nome, e o backend nao tem fallback. A
   * linha precisa ser identificavel de algum jeito para o operador saber para quem esta ligando.
   */
  nomeDaEmpresaEmRisco(linha: MasterEmpresaEmRiscoDTO): string {
    return linha?.empresaNome
      || linha?.ruc
      || linha?.email
      || this.translate.instant('master.planos.dashboard.drilldown.empresa_sem_nome', {
        id: linha?.empresaId ?? '',
      });
  }

  // =========================================================================
  // Drill-down de dinheiro
  // =========================================================================

  /**
   * Abre a lista por tras de "Total recebido" (RECEBIDAS) ou "Cobranca emitida e nao paga"
   * (PENDENTES). O periodo enviado e o ECOADO pelo dashboard, e nao o do filtro: e o que faz a
   * soma da lista ser o valor do card.
   */
  onCardCobrancaClick(tipo: TipoCobrancaMaster): void {
    if (this.dashboardEstado !== 'pronto' || !this.dashboard) return;
    this.cobrancaTipo = tipo;
    this.cobrancaDialogVisivel = true;
    this.carregarCobrancas();
  }

  carregarCobrancas(): void {
    if (!this.dashboard) return;
    this.cobrancaEstado = 'carregando';
    this.cobrancaErro = '';
    this.cobrancaLinhas = [];

    this.masterEmpresaService
      .listarCobrancas({
        tipo: this.cobrancaTipo,
        de: this.dashboard.periodoDe,
        ate: this.dashboard.periodoAte,
      })
      .subscribe({
        next: (linhas) => {
          this.cobrancaLinhas = linhas ?? [];
          this.cobrancaEstado = this.cobrancaLinhas.length === 0 ? 'vazio' : 'pronto';
        },
        error: (err: HttpErrorResponse) => {
          this.cobrancaErro = this.mensagemDeErroDoDashboard(err);
          this.cobrancaEstado = 'erro';
        },
      });
  }

  fecharCobrancas(): void {
    this.cobrancaDialogVisivel = false;
    this.cobrancaLinhas = [];
    this.cobrancaEstado = 'carregando';
  }

  /** Soma da lista — e o numero do card, exibido no rodape para o operador conferir. */
  get totalDasCobrancas(): number {
    return this.cobrancaLinhas.reduce((soma, linha) => soma + (Number(linha.valor) || 0), 0);
  }

  /** "Plano Oro" ou "Usuarios adicionais (6)": o que aquele dinheiro pagou. */
  origemDaCobranca(linha: MasterCobrancaDTO): string {
    if (linha.origem === 'USUARIOS_ADICIONAIS') {
      return linha.usuarios
        ? this.translate.instant('master.planos.dashboard.cobrancas.usuarios_qtd', { n: linha.usuarios })
        : this.translate.instant('master.planos.dashboard.cobrancas.usuarios');
    }
    return this.translate.instant('master.planos.dashboard.cobrancas.plano', {
      plano: linha.planoNome ?? '',
    });
  }

  /** O enum cru (`QR_VENDAS`, `T_PAGO`) nao e para o operador ler. */
  formaDePagamento(tipo?: string | null): string {
    if (tipo === 'QR_VENDAS') return 'QR';
    if (tipo === 'T_PAGO') return 'T-Pago';
    return tipo || '—';
  }

  abrirEmpresaDaCobranca(linha: MasterCobrancaDTO): void {
    if (linha?.empresaId == null) return;
    this.router.navigate(['/master/empresas', linha.empresaId], {
      queryParams: { origem: 'planos' },
    });
  }

  nomeDaEmpresaDaCobranca(linha: MasterCobrancaDTO): string {
    return linha?.empresaNome
      || linha?.ruc
      || linha?.email
      || this.translate.instant('master.planos.dashboard.drilldown.empresa_sem_nome', {
        id: linha?.empresaId ?? '',
      });
  }
}
